import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  getDoc,
  doc
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import logo from '../assets/logo.png';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { ProssimiShowBanner } from '../components/show/ProssimiShowBanner';
import NewsBanner from '../components/home/NewsBanner';
import UserHeader from '../components/home/UserHeader';
import QuickInfoCards from '../components/home/QuickInfoCards';
import TopWrestlersSection from '../components/home/TopWrestlersSection';
import UltimoShowCard from '../components/home/UltimoShowCard';
import SectionAd from '../components/ads/SectionAd';
import SeasonCreationBanner from '../components/SeasonCreationBanner';
import { buildFactionMemberFields, getFactionStoredWrestlers } from '../utils/factionWrestlers';
import { calculateFactionTotalScore } from '../utils/factionScoring';
import { calculateGmMatchPointsByFaction, getFactionGmMatches, normalizeGmMatch } from '../utils/gmMatches';
import { readFavoriteFactionSelection } from '../utils/favoriteFactionStorage';
import { isShowInLiveWindow, toShowDate } from '../utils/showLiveWindow';
import { autoCloseExpiredGmMatches } from '../utils/gmMatchSync';
import {
  buildFactionLastShowSummary,
  findLatestScoredShowEntry
} from '../utils/lastShowSummary';

function Home() {
  const [lastShow, setLastShow] = useState(null);
  const [lastShowParticipations, setLastShowParticipations] = useState([]);
  const [lastShowEntries, setLastShowEntries] = useState([]);
  const [upcomingShows, setUpcomingShows] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [userStats, setUserStats] = useState(null);
  const [userFaction, setUserFaction] = useState(null);
  const [userGmMatches, setUserGmMatches] = useState([]);
  const [nextShowInfo, setNextShowInfo] = useState(null);
  const [topWrestlersLastShow, setTopWrestlersLastShow] = useState([]);
  const [loadingUserData, setLoadingUserData] = useState(false);

  const [allWrestlers, setAllWrestlers] = useState([]);
  const [nextShowParticipants, setNextShowParticipants] = useState([]);

  // Countdown per la fine della stagione 0
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Aggiungi questo stato per controllare se TUTTO è caricato

  // Carica tutti i wrestler quando l'utente è loggato
  useEffect(() => {
    const loadAllWrestlers = async () => {
      try {
        const wrestlersSnap = await getDocs(collection(db, 'wrestlers'));
        const wrestlersData = wrestlersSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAllWrestlers(wrestlersData);
      } catch (err) {
        console.error('Errore caricamento wrestler:', err);
      }
    };

    if (auth.currentUser) {
      loadAllWrestlers();
    }
  }, [auth.currentUser]);

  // Carica i partecipanti al prossimo show
  useEffect(() => {
    const loadNextShowParticipants = async () => {
      if (nextShowInfo?.id) {
        try {
          const participantsSnap = await getDocs(
            collection(db, 'shows', nextShowInfo.id, 'participations')
          );
          const participantIds = participantsSnap.docs.map(doc => doc.data().wrestlerId);
          setNextShowParticipants(participantIds);
        } catch (err) {
          console.error('Errore caricamento partecipanti:', err);
        }
      }
    };

    if (nextShowInfo?.id) {
      loadNextShowParticipants();
    }
  }, [nextShowInfo]);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      if (!u) return;
      try {
        await getDocs(query(collection(db, 'pollResponses'), where('uid', '==', u.uid)));
      } catch (err) {
        console.warn('Errore verificando sondaggio (home):', err);
      }
    });

    return () => unsubAuth && unsubAuth();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const now = new Date();
        const upcomingLookupStart = new Date(now);
        upcomingLookupStart.setHours(upcomingLookupStart.getHours() - 4);

        const upcomingShowsQuery = query(
          collection(db, 'shows'),
          where('data', '>=', upcomingLookupStart),
          orderBy('data', 'asc')
        );

        const [latestShowEntry, upcomingShowsSnap] = await Promise.all([
          findLatestScoredShowEntry({
            db,
            referenceDate: now,
            candidateLimit: 20
          }),
          getDocs(upcomingShowsQuery)
        ]);

        if (latestShowEntry?.show) {
          setLastShow(latestShowEntry.show);
          setLastShowParticipations(latestShowEntry.participations || []);
          setLastShowEntries(latestShowEntry.showEntries || []);

          const wrestlerNames = {};
          const wrestlersSnap = await getDocs(collection(db, 'wrestlers'));
          wrestlersSnap.docs.forEach(wDoc => {
            const wrestlerData = wDoc.data();
            wrestlerNames[wDoc.id] = wrestlerData.name || wrestlerData.nome;
          });

          setParticipants(
            (latestShowEntry.participations || []).map(participation => ({
              id: participation.wrestlerId,
              name: wrestlerNames[participation.wrestlerId] || 'Sconosciuto',
              points: participation.totalPoints
            }))
          );
        } else {
          setLastShow(null);
          setLastShowParticipations([]);
          setLastShowEntries([]);
          setParticipants([]);
        }

        const upcoming = [];
        upcomingShowsSnap.forEach(docSnap => {
          const data = docSnap.data();
          const showDate = toShowDate(data.data);
          if (!showDate) return;

          if (showDate >= now || isShowInLiveWindow(showDate, now)) {
            upcoming.push({
              id: docSnap.id,
              nome: data.nome,
              logo: data.logo || '',
              sfondo: data.sfondo || '',
              data: showDate,
              type: data.type || 'weekly'
            });
          }
        });
        setUpcomingShows(upcoming);

      } catch (err) {
        console.error("Errore nel caricamento dei dati:", err);
        setError(err.message || "Errore sconosciuto durante il caricamento");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        setError("Il caricamento sta impiegando più tempo del previsto");
        setLoading(false);
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [loading]);

  // Calcola il prossimo show info
  useEffect(() => {
    if (upcomingShows.length > 0) {
      const nextShow = upcomingShows[0];
      const now = new Date();
      const timeDiff = nextShow.data.getTime() - now.getTime();
      const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

      setNextShowInfo({
        ...nextShow,
        daysLeft: days,
        hoursLeft: hours,
        formattedDate: nextShow.data.toLocaleDateString('it-IT', {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        })
      });
    }
  }, [upcomingShows]);

  // Carica i dati dell'utente
  useEffect(() => {
    const loadUserData = async () => {
      const user = auth.currentUser;
      if (!user) {
        // Se l'utente non è loggato, i dati sono comunque completi
        return;
      }

      setLoadingUserData(true);
      try {
        // 1. Carica dati utente
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        let userFactionData = null;

        if (userDocSnap.exists()) {
          const userDataFromFirestore = userDocSnap.data();

          // 2. CARICA TUTTE LE FAZIONI
          const factionsSnap = await getDocs(collection(db, 'factions'));
          const allFactions = factionsSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          const cachedFavoriteSelection = readFavoriteFactionSelection(user.uid);
          const hasCachedFavoritePreference = Boolean(
            cachedFavoriteSelection &&
            Object.prototype.hasOwnProperty.call(cachedFavoriteSelection, 'favoriteFactionId')
          );
          const resolvedFavoriteFactionId = hasCachedFavoritePreference
            ? cachedFavoriteSelection.favoriteFactionId
            : userDataFromFirestore.favoriteFactionId || null;
          const userFactionIds = Array.isArray(userDataFromFirestore.factionIds)
            ? userDataFromFirestore.factionIds.filter(Boolean)
            : [];
          const buildDirectHomeFactionData = (factionSource) => {
            const factionMembers = getFactionStoredWrestlers(factionSource);

            return {
              id: factionSource.id,
              name: factionSource.name || 'Senza nome',
              imageUrl: factionSource.imageUrl || '',
              rosterId: factionSource.rosterId || null,
              roster: factionSource.roster || null,
              isGM: Boolean(factionSource.isGM),
              points: factionSource.totalScore || 0,
              accumulatedPoints: factionSource.accumulatedPoints || 0,
              malus: factionSource.malus || 0,
              creditsLeft: factionSource.creditsLeft || 0,
              ownerUid: factionSource.ownerUid || null,
              currentLineup: factionSource.currentLineup || null,
              lineup: factionSource.lineup || null,
              lineupHistory: Array.isArray(factionSource.lineupHistory) ? factionSource.lineupHistory : [],
              ...buildFactionMemberFields(factionMembers)
            };
          };
          const cachedFavoriteFaction = cachedFavoriteSelection?.faction?.id
            ? cachedFavoriteSelection.faction
            : null;
          const directUserFaction =
            userFactionIds.length > 0
              ? (
                  [resolvedFavoriteFactionId, ...userFactionIds]
                    .filter(Boolean)
                    .map(factionId => (
                      allFactions.find(faction => faction.id === factionId) ||
                      (cachedFavoriteFaction?.id === factionId ? cachedFavoriteFaction : null)
                    ))
                    .find(Boolean) || null
                )
              : null;

          if (directUserFaction) {
            userFactionData = buildDirectHomeFactionData(directUserFaction);
            setUserFaction(userFactionData);
          }

          const rostersSnap = await getDocs(collection(db, 'rosters'));
          const rostersMap = {};
          rostersSnap.docs.forEach(rosterDoc => {
            rostersMap[rosterDoc.id] = { id: rosterDoc.id, ...rosterDoc.data() };
          });

          const gmScoresByRoster = {};
          await Promise.all(
            Object.values(rostersMap)
              .filter(roster => roster.isGM)
              .map(async roster => {
                const matchesSnap = await getDocs(collection(db, 'rosters', roster.id, 'matches'));
                const normalizedMatches = matchesSnap.docs.map(docSnap => normalizeGmMatch(docSnap));
                const { matches } = await autoCloseExpiredGmMatches({
                  db,
                  rosterId: roster.id,
                  matches: normalizedMatches,
                  roster,
                  persist: false
                });
                gmScoresByRoster[roster.id] = calculateGmMatchPointsByFaction(matches);
              })
          );

          // 3. Carica punteggi dei wrestler
          const wrestlersSnap = await getDocs(collection(db, 'wrestlers'));
          const wrestlerScores = {};
          wrestlersSnap.forEach(doc => {
            wrestlerScores[doc.id] = doc.data().score || 0;
          });

          // 4. Calcola punteggi per tutte le fazioni
          const factionsWithScores = allFactions
            .map(faction => {
              try {
                const factionWrestlers = getFactionStoredWrestlers(faction);
                const rosterMeta = faction.rosterId
                  ? rostersMap[faction.rosterId]
                  : Object.values(rostersMap).find(roster => roster.name === faction.roster);
                const isGmRoster = Boolean(rosterMeta?.isGM);
                const totalScore = isGmRoster
                  ? (gmScoresByRoster[rosterMeta?.id]?.[faction.id] || 0)
                  : calculateFactionTotalScore({
                      faction,
                      roster: rosterMeta,
                      members: factionWrestlers,
                      wrestlerScoresById: wrestlerScores,
                    });

                return {
                  ...faction,
                  totalScore,
                  roster: rosterMeta?.name || faction.roster || 'Indy',
                  rosterId: rosterMeta?.id || faction.rosterId || null,
                  isGM: isGmRoster,
                  hasBackstage: rosterMeta?.hasBackstage,
                  backstageCount: rosterMeta?.backstageCount,
                  hasMainEventer: rosterMeta?.hasMainEventer
                };
              } catch (factionScoreError) {
                console.warn('Errore calcolo punteggio fazione:', faction?.id, factionScoreError);
                return {
                  ...faction,
                  totalScore: 0,
                  roster: faction.roster || 'Indy',
                  rosterId: faction.rosterId || null,
                  isGM: false,
                  hasBackstage: false,
                  backstageCount: 0,
                  hasMainEventer: false
                };
              }
            })
            .filter(Boolean);

          // 5. Ordina per punteggio (decrescente)
          const sortedFactions = [...factionsWithScores].sort((a, b) => b.totalScore - a.totalScore);
          const userFactions = userFactionIds
            .map(factionId => (
              sortedFactions.find(f => f.id === factionId) ||
              factionsWithScores.find(f => f.id === factionId) ||
              allFactions.find(f => f.id === factionId)
            ))
            .filter(Boolean);

          // 6. Trova la fazione dell'utente - MODIFICA QUESTA PARTE
          let userFactionId = null;
          let userFactionFromList = null;

          // DEBUG: Stampa gli ID disponibili
          console.log("DEBUG - ID disponibili nell'utente:", {
            favoriteFactionId: resolvedFavoriteFactionId,
            factionId: userDataFromFirestore.factionId,
            roadToFactionId: userDataFromFirestore.roadToFactionId,
            factionIds: userFactionIds
          });

          // PRIMA verifica se c'Ã¨ una fazione PREFERITA valida tra le fazioni dell'utente
          if (
            resolvedFavoriteFactionId &&
            userFactionIds.includes(resolvedFavoriteFactionId)
          ) {
            userFactionId = resolvedFavoriteFactionId;
            userFactionFromList =
              sortedFactions.find(f => f.id === resolvedFavoriteFactionId) ||
              factionsWithScores.find(f => f.id === resolvedFavoriteFactionId) ||
              allFactions.find(f => f.id === resolvedFavoriteFactionId) ||
              (cachedFavoriteFaction?.id === resolvedFavoriteFactionId ? cachedFavoriteFaction : null);
            console.log("Trovata fazione PREFERITA:", userFactionId, userFactionFromList?.name);
          }
          // POI usa la prima fazione dell'array dell'utente
          if (!userFactionFromList && userFactions.length > 0) {
            userFactionId = userFactions[0].id;
            userFactionFromList = userFactions[0];
            console.log("Fallback prima fazione utente:", userFactionId, userFactionFromList?.name);
          }

          // 7. Calcola posizione globale
          let userFactionPosition = '-';
          let userFactionScore = 0;
          let userFactionRoster = null;
          let userRosterPosition = '-';

          if (userFactionFromList) {
            // Trova la posizione nella classifica globale
            const userFactionIndex = sortedFactions.findIndex(f => f.id === userFactionId);
            if (userFactionIndex !== -1) {
              // Calcola la posizione considerando punteggi uguali
              let position = userFactionIndex + 1;
              for (let i = userFactionIndex - 1; i >= 0; i--) {
                if (sortedFactions[i].totalScore === sortedFactions[userFactionIndex].totalScore) {
                  position = i + 1;
                } else {
                  break;
                }
              }
              userFactionPosition = position;
              userFactionScore = userFactionFromList.totalScore;
              userFactionRoster = userFactionFromList.roster;

              // 8. Calcola posizione nel roster
              if (userFactionRoster) {
                const rosterFactions = sortedFactions.filter(f => f.roster === userFactionRoster);
                const userRosterIndex = rosterFactions.findIndex(f => f.id === userFactionId);
                if (userRosterIndex !== -1) {
                  let rosterPosition = userRosterIndex + 1;
                  for (let i = userRosterIndex - 1; i >= 0; i--) {
                    if (rosterFactions[i].totalScore === rosterFactions[userRosterIndex].totalScore) {
                      rosterPosition = i + 1;
                    } else {
                      break;
                    }
                  }
                  userRosterPosition = rosterPosition;
                }
              }
            }

            const buildHomeFactionData = (factionSource, factionMembers = []) => ({
              id: factionSource.id || userFactionId,
              name: factionSource.name || 'Senza nome',
              imageUrl: factionSource.imageUrl || '',
              rosterId: factionSource.rosterId || userFactionFromList?.rosterId || null,
              roster: factionSource.roster || userFactionRoster || userFactionFromList?.roster || null,
              isGM: Boolean(factionSource.isGM ?? userFactionFromList?.isGM),
              points: factionSource.totalScore ?? userFactionScore,
              accumulatedPoints: factionSource.accumulatedPoints || 0,
              malus: factionSource.malus || 0,
              creditsLeft: factionSource.creditsLeft || 0,
              ownerUid: factionSource.ownerUid,
              currentLineup: factionSource.currentLineup || userFactionFromList?.currentLineup || null,
              lineup: factionSource.lineup || userFactionFromList?.lineup || null,
              lineupHistory: Array.isArray(factionSource.lineupHistory)
                ? factionSource.lineupHistory
                : Array.isArray(userFactionFromList?.lineupHistory)
                  ? userFactionFromList.lineupHistory
                  : [],
              ...buildFactionMemberFields(factionMembers)
            });

            // 9. Carica dettagli fazione utente
            try {
              const factionDoc = await getDoc(doc(db, 'factions', userFactionId));
              if (factionDoc.exists()) {
                const data = factionDoc.data();
                const factionWrestlers = getFactionStoredWrestlers(data);

                // Prepara dati fazione per la Home
                userFactionData = buildHomeFactionData(
                  {
                    id: factionDoc.id,
                    ...data,
                    roster: userFactionRoster,
                    isGM: userFactionFromList?.isGM,
                    totalScore: userFactionScore
                  },
                  factionWrestlers
                );

                if (userFactionData.rosterId) {
                  const matchesSnap = await getDocs(collection(db, 'rosters', userFactionData.rosterId, 'matches'));
                  const normalizedMatches = matchesSnap.docs.map(docSnap => normalizeGmMatch(docSnap));
                  const { matches } = await autoCloseExpiredGmMatches({
                    db,
                    rosterId: userFactionData.rosterId,
                    matches: normalizedMatches,
                    persist: true
                  });
                  setUserGmMatches(getFactionGmMatches(matches, factionDoc.id));
                } else {
                  setUserGmMatches([]);
                }
              } else if (userFactionFromList) {
                const fallbackMembers = getFactionStoredWrestlers(userFactionFromList);
                userFactionData = buildHomeFactionData(userFactionFromList, fallbackMembers);
                setUserGmMatches([]);
              }
            } catch (factionErr) {
              console.warn("Errore caricamento fazione:", factionErr);
              if (userFactionFromList) {
                const fallbackMembers = getFactionStoredWrestlers(userFactionFromList);
                userFactionData = buildHomeFactionData(userFactionFromList, fallbackMembers);
              }
              setUserGmMatches([]);
            }
          }

          // 10. Imposta userStats
          setUserStats({
            name: userDataFromFirestore.username || userDataFromFirestore.email?.split('@')[0] || 'Utente',
            position: userFactionPosition,
            totalPoints: userFactionScore,
            credits: userDataFromFirestore.credits || 0,
            pointsChange: 0,
            lastShowPosition: 'N/A',
            email: userDataFromFirestore.email,
            uid: user.uid,
            factionId: userFactionId,
            rosterPosition: userRosterPosition,
            roster: userFactionRoster,
            favoriteFactionId: resolvedFavoriteFactionId,
          });

          setUserFaction(userFactionData);
          if (!userFactionData) {
            setUserGmMatches([]);
          }
        }

        // 11. Carica top 3 wrestler dell'ultimo show
        if (lastShow && participants.length > 0) {
          const top3 = [...participants]
            .sort((a, b) => b.points - a.points)
            .slice(0, 3);
          setTopWrestlersLastShow(top3);
        }


      } catch (err) {
        console.error("Errore caricamento dati utente:", err);
      } finally {
        setLoadingUserData(false);
      }
    };

    if (!loading && auth.currentUser) {
      loadUserData();
      // Se non è loggato e i dati di base sono caricati
    }
  }, [loading, auth.currentUser, lastShow, participants, upcomingShows]);

  // Controlla quando TUTTI i dati sono pronti
  // useEffect per il countdown alla fine della stagione 0
  useEffect(() => {
    const targetDate = new Date('2026-04-20T00:00:00');

    const interval = setInterval(() => {
      const now = new Date();
      const diff = targetDate - now;

      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown({ days, hours, minutes, seconds });
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // MOSTRA SOLO LO SPINNER FINCHÉ NON SONO CARICATI TUTTI I DATI
  const lastShowFactionSummary = useMemo(
    () =>
      buildFactionLastShowSummary({
        faction: userFaction,
        participations: lastShowParticipations,
        allWrestlers,
        show: lastShow,
        showEntries: lastShowEntries
      }),
    [allWrestlers, lastShow, lastShowEntries, lastShowParticipations, userFaction]
  );

  const showInitialLoader =
    loading && !error && !lastShow && upcomingShows.length === 0;

  if (showInitialLoader) {
    return <LoadingSpinner fullPage={true} />;
  }

  const isUserDataPending =
    Boolean(auth.currentUser) && (loadingUserData || !userStats);

  const getFactionToDisplay = () => {
    // Debug
    console.log("getFactionToDisplay - DEBUG:");
    console.log("- userStats.favoriteFactionId:", userStats?.favoriteFactionId);
    console.log("- userFaction.id:", userFaction?.id);
    console.log("- Sono uguali?", userFaction?.id === userStats?.favoriteFactionId);

    // userFaction dovrebbe essere già la fazione preferita (se esiste) 
    // grazie alla logica in loadUserData
    return userFaction;
  };

  // Se c'è un errore, mostra l'errore
  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }
/*
  const HomeIntroText = () => (
    <section className="container my-5">
      <div className="row justify-content-center">
        <div className="col-lg-10 col-xl-8">
          <SeasonAnnouncementBanner
            className="season-announcement-banner--inline mb-4"
            title={SEASON_ANNOUNCEMENT.title}
            message="La Stagione 0 si è conclusa. Dal 1° maggio inizierà la Stagione 1 e sarà possibile creare la propria fazione!"
          />
          <div className="card border-0 rounded-4 overflow-hidden mb-5">
            <div className="card-body p-1">
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="d-flex align-items-start mb-4">
                    <div className="bg-brand-primary-ultralight bg-opacity-10 p-2 rounded-3 me-3">
                      <i className="bi bi-people-fill text-brand-primary"></i>
                    </div>
                    <div>
                      <h4 className="fw-bold text-dark mb-2">Costruisci la tua Fazione</h4>
                      <p className="text-muted mb-0">
                        Dal 1° maggio potrai creare la tua fazione!
                      </p>
                    </div>
                  </div>

                  <div className="d-flex align-items-start mb-4">
                    <div className="bg-warning bg-opacity-10 p-2 rounded-3 me-3">
                      <i className="bi bi-graph-up-arrow text-warning"></i>
                    </div>
                    <div>
                      <h4 className="fw-bold text-dark mb-2">Segui le Performance Reali</h4>
                      <p className="text-muted mb-0">
                        Guadagna punti in base alle vittorie, match importanti e storyline degli show settimanali.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="d-flex align-items-start mb-4">
                    <div className="bg-success bg-opacity-10 p-2 rounded-3 me-3">
                      <i className="bi bi-trophy text-success"></i>
                    </div>
                    <div>
                      <h4 className="fw-bold text-dark mb-2">Scala la calssifica</h4>
                      <p className="text-muted mb-0">
                        Sfida altri giocatori e diventa il campione del FantaWrestling.
                      </p>
                    </div>
                  </div>

                  <div className="d-flex align-items-start mb-4">
                    <div className="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
                      <i className="bi bi-heart-pulse text-primary"></i>
                    </div>
                    <div>
                      <h4 className="fw-bold text-dark mb-2">Strategia in Tempo Reale</h4>
                      <p className="text-muted mb-0">
                        Gestisci infortuni, effettua scambi strategici e adatta la tua fazione agli eventi.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center mt-5">
                <div className="row g-3 justify-content-center align-items-center">
                  <div className="col-12 col-sm-6">
                    <Link to="/rules" className="btn btn-outline-dark btn-lg d-flex align-items-center justify-content-center py-2 w-100">
                      <i className="bi bi-book me-2 d-none d-sm-inline"></i>
                      <i className="bi bi-book me-2 d-inline d-sm-none"></i>
                      <span>Leggi le regole</span>
                    </Link>
                  </div>
                  <div className="col-12 col-sm-6">
                    <Link to="/auth" className="btn btn-brand-primary-light btn-lg d-flex align-items-center justify-content-center py-2 w-100">
                      <i className="bi bi-play-fill me-2 d-none d-sm-inline"></i>
                      <i className="bi bi-play-fill me-2 d-inline d-sm-none"></i>
                      <span>Inizia Ora - Gratis!</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
*/
  // Componente per le statistiche della community
  const CommunityStats = () => (
    <section className="container my-5">
      <div className="text-center mb-5">
        <h2 className="fw-bold text-dark display-5 mb-3">
          La Community FantaWrestling
        </h2>
        <p className="lead text-muted">
          Unisciti a centinaia di appassionati che vivono il wrestling in modo unico
        </p>
      </div>

      <div className="row g-4">
        <div className="col-md-3 col-sm-6">
          <div className="card border-0 shadow-sm h-100 text-center py-4">
            <div className="card-body">
              <i className="bi bi-people display-4 text-primary mb-3"></i>
              <h3 className="fw-bold text-dark">300+</h3>
              <p className="text-muted mb-0">Giocatori Attivi</p>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6">
          <div className="card border-0 shadow-sm h-100 text-center py-4">
            <div className="card-body">
              <i className="bi bi-shield-check display-4 text-success mb-3"></i>
              <h3 className="fw-bold text-dark">30+</h3>
              <p className="text-muted mb-0">Leghe Attive</p>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6">
          <div className="card border-0 shadow-sm h-100 text-center py-4">
            <div className="card-body">
              <i className="bi bi-person-badge display-4 text-warning mb-3"></i>
              <h3 className="fw-bold text-dark">150+</h3>
              <p className="text-muted mb-0">Wrestler disponibili</p>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6">
          <div className="card border-0 shadow-sm h-100 text-center py-4">
            <div className="card-body">
              <i className="bi bi-calendar-event display-4 text-danger mb-3"></i>
              <h3 className="fw-bold text-dark">2</h3>
              <p className="text-muted mb-0">Show a Settimana</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  // Footer migliorato
  const EnhancedFooter = () => (
    <footer className="hero-footer bg-dark text-light py-5 mt-5 fs-6">
      <div className="container">
        <div className="row">
          <div className="col-md-4 mb-4">
            <p className="text-light opacity-75">
              Il fantagame per veri appassionati di wrestling.
              Strategia, competizione e community in un'unica esperienza.
            </p>
            <div className="social-links mt-4">
              <a href="https://www.instagram.com/fanta.wrestling/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-light me-3 fs-5">
                <i className="bi bi-instagram"></i>
              </a>
              <a href="https://t.me/fantawrestlingchat"
                target="_blank"
                rel="noopener noreferrer"
                className="text-light me-3 fs-5">
                <i className="bi bi-telegram"></i>
              </a>
              <a href="mailto:info@fantawrestling.com"
                className="text-light fs-5">
                <i className="bi bi-envelope"></i>
              </a>
            </div>
          </div>

          <div className="col-md-4 mb-4">
            <h6 className="fw-bold text-warning mb-4">Link Utili</h6>
            <div className="d-flex flex-column">
              <Link to="/rules" className="text-light mb-2 text-decoration-none opacity-75 hover-opacity-100">
                <i className="bi bi-book me-2"></i>
                Regolamento Completo
              </Link>
              <Link to="/about" className="text-light mb-2 text-decoration-none opacity-75 hover-opacity-100">
                <i className="bi bi-info-circle me-2"></i>
                Chi Siamo
              </Link>
              <Link to="/privacy" className="text-light mb-2 text-decoration-none opacity-75 hover-opacity-100">
                <i className="bi bi-shield-check me-2"></i>
                Privacy Policy
              </Link>
              <Link to="/faq" className="text-light mb-2 text-decoration-none opacity-75 hover-opacity-100">
                <i className="bi bi-question-circle me-2"></i>
                FAQ
              </Link>
            </div>
          </div>

          <div className="col-md-4 mb-4">
            <h5 className="fw-bold text-warning mb-4">Avviso Legale</h5>
            <p className="text-light opacity-75 small">
              FantaWrestling è un progetto indipendente realizzato da appassionati per appassionati.
              Non siamo affiliati con nessuna federazione di wrestling.
              Tutti i nomi di wrestler e show sono utilizzati a solo scopo informativo e di gioco.
            </p>
          </div>
        </div>

        <hr className="border-secondary my-4" />

        <div className="text-center opacity-75 small">
          <p className="mb-0">
            &copy; {new Date().getFullYear()} FantaWrestling. Tutti i diritti riservati.
          </p>
        </div>
      </div>
    </footer>
  );

  // SOLO QUANDO TUTTO È CARICATO, mostra la Home completa
  return (
    <div className="home-container">

{/* Hero Section - Versione diversa per utenti loggati e non */}
<div className="hero">
  <div className="d-flex flex-column align-items-center">
    {/* Logo visibile solo per utenti non loggati */}
    {!auth.currentUser && (
      <img src={logo} alt="Logo" className="logo" />
    )}

    {auth.currentUser ? (
      // Contenuto per utenti loggati (senza logo)
      <div className="hero-content text-center mt-3">
        <h1 className="hero-title fw-bold mb-4" style={{
          fontFamily: "'Cinzel Decorative'",
          textShadow: '1px 1px 2px rgba(0, 0, 0)'
        }}>
          {userStats ? `CIAO, ${userStats.name}` : 'Benvenuto'}
        </h1>
      </div>
    ) : (
      // Contenuto per utenti non loggati
      <div className="hero-content text-center mt-3">
        <h1 className="hero-title fw-bold mb-3" style={{
          fontFamily: "'Cinzel Decorative'",
          textShadow: '1px 1px 2px rgba(0, 0, 0)'
        }}>fAnTaWreSTlIng</h1>
        <p className="hero-subtitle mb-2">Benvenuto nel FantaWrestling!</p>
        
        <div className="social-links mt-2">
          <a href="https://www.instagram.com/fanta.wrestling/" target="_blank" rel="noopener noreferrer" className="social-icon" title="Instagram">
            <i className="bi bi-instagram"></i>
          </a>
          <a href="https://t.me/fantawrestlingchat" target="_blank" rel="noopener noreferrer" className="social-icon" title="Telegram">
            <i className="bi bi-telegram"></i>
          </a>
        </div>
        <p className="mt-2">Seguici e unisciti alla community!</p>
      </div>
    )}
  </div>
</div>

      {/* Countdown per la fine della stagione 0 */}
      {(countdown.days > 0 || countdown.hours > 0 || countdown.minutes > 0 || countdown.seconds > 0) && (
        <>
          <div className="container my-4">
            <div className="row justify-content-center align-items-center">
              <div className="col-lg-8">
                <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #000000, #0042bd)' }}>
                  <div className="card-body text-center py-4">
                    <h3 className="text-white fw-bold mb-3">Termina la stagione 0!</h3>
                    <p className="text-white mb-4">La stagione 0 sta per concludersi, seguici sui social per rimanere informato sulle novità</p>
                    <div className="row g-3 justify-content-center">
                      <div className="col-auto">
                        <div className="bg-trasparent rounded-3 p-1">
                          <div className="h2 text-white mb-0 fw-bold">{countdown.days}</div>
                          <small className="text-white opacity-75">Giorni</small>
                        </div>
                      </div>
                      <div className="col-auto">
                        <div className="bg-trasparent rounded-3 p-1">
                          <div className="h2 text-white mb-0 fw-bold">{countdown.hours}</div>
                          <small className="text-white opacity-75">Ore</small>
                        </div>
                      </div>
                      <div className="col-auto">
                        <div className="bg-trasparent rounded-3 p-1">
                          <div className="h2 text-white mb-0 fw-bold">{countdown.minutes}</div>
                          <small className="text-white opacity-75">Minuti</small>
                        </div>
                      </div>
                      <div className="col-auto">
                        <div className="bg-trasparent rounded-3 p-1">
                          <div className="h2 text-white mb-0 fw-bold">{countdown.seconds}</div>
                          <small className="text-white opacity-75">Secondi</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <SectionAd placement="home" className="d-flex justify-content-center my-2" />
        </>
      )}

      <div className="container py-3">
        <SeasonCreationBanner userEmail={auth.currentUser?.email} className="mb-4" />

        {/* VISUALIZZAZIONE UTENTE LOGGATO */}
        {auth.currentUser ? (
          <>
            {/* Header utente loggato */}
            {userStats ? (
              <div
                className="home-reveal is-visible"
                style={{ '--home-delay': '30ms' }}
              >
                <UserHeader stats={userStats} navigate={navigate} />
              </div>
            ) : isUserDataPending ? (
              <div
                className="home-reveal is-visible home-skeleton home-skeleton--header mb-4"
                style={{ '--home-delay': '30ms' }}
                aria-hidden="true"
              />
            ) : null}

            {/* Ultimo show */}
            {lastShow && (
              <div
                className="mb-5 home-reveal is-visible"
                style={{ '--home-delay': '90ms' }}
              >
                <UltimoShowCard
                  show={lastShow}
                  participants={participants}
                  navigate={navigate}
                />
              </div>
            )}
            {/* Card informazioni rapide */}
            {userStats ? (
              <div
                className="home-reveal is-visible"
                style={{ '--home-delay': '150ms' }}
              >
                <QuickInfoCards
                  userFaction={getFactionToDisplay()}
                  nextShowInfo={nextShowInfo}
                  userStats={userStats}
                  allWrestlers={allWrestlers}
                  nextShowParticipants={nextShowParticipants}
                  gmMatches={userGmMatches}
                  lastShowSummary={lastShowFactionSummary}
                />
              </div>
            ) : isUserDataPending ? (
              <div
                className="home-reveal is-visible home-skeleton home-skeleton--panel mb-4"
                style={{ '--home-delay': '150ms' }}
                aria-hidden="true"
              />
            ) : null}
            
            <SectionAd placement="home" className="d-flex justify-content-center my-2" />

            <div
              className="home-reveal is-visible"
              style={{ '--home-delay': '210ms' }}
            >
              <NewsBanner />
            </div>


            {/* Banner prossimi show */}
            {upcomingShows.length > 0 && (
              <div
                className="home-reveal is-visible"
                style={{ '--home-delay': '270ms' }}
              >
                <ProssimiShowBanner shows={upcomingShows} />
              </div>
            )}


          </>
        ) : (
          /* VISUALIZZAZIONE UTENTE NON LOGGATO */
          <>
          {/*<HomeIntroText />

            <CommunityStats />
            */}

            <NewsBanner />

            {/* Ultimo show dinamico */}
            {lastShow ? (
              <UltimoShowCard
                show={lastShow}
                participants={participants}
                navigate={navigate}
              />
            ) : null}

            {/* Banner prossimi show */}
            {upcomingShows.length > 0 && (
              <ProssimiShowBanner shows={upcomingShows} />
            )}
            <SectionAd placement="home" className="d-flex justify-content-center my-2" />
            {/* Top wrestler e azioni rapide */}
            <TopWrestlersSection
              topWrestlersLastShow={topWrestlersLastShow}
              userFaction={userFaction}
            />
          </>
        )}

        <section className="home-support-callout mt-4 mt-lg-5">
          <div className="home-support-callout__panel">
            <div>
              <span className="home-support-callout__eyebrow">
                Supporta Fantawrestling
              </span>
              <h2 className="home-support-callout__title">
                Se ti piace il progetto, puoi aiutarci a farlo crescere e sostenere il Premium
              </h2>
              <p className="home-support-callout__text mb-0">
                Un contributo ci aiuta a sostenere i costi, migliorare il sito
                e preparare le funzionalita premium per tutta la community.
              </p>
            </div>

            <div className="home-support-callout__actions">
              <Link to="/premium" className="btn btn-brand-secondary home-support-callout__primary">
                <i className="bi bi-stars me-2"></i>
                Vai alla pagina Premium
              </Link>
              <a
                href="https://paypal.me/fantawrestling"
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-brand-secondary home-support-callout__secondary"
              >
                <i className="bi bi-paypal me-2"></i>
                Apri PayPal
              </a>
            </div>
          </div>
        </section>
      </div>
      <SectionAd placement="homeBottom" />
      {/*<EnhancedFooter />*/}


    </div>
  );
}

export default Home;
